import { User } from "lucide-react"
import type { DispatcherStats } from "../data/types"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { getIntlLocale } from "@/lib/i18n/format"

interface BiuroTabProps {
  dispatchers: DispatcherStats[]
}

const formatNumber = (value: number) => {
  return new Intl.NumberFormat(getIntlLocale("pl-PL")).format(value)
}

const formatTime = (hours: number) => {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  if (m === 0) return `${h} h`
  return `${h} h ${m} min`
}

export function BiuroTab({ dispatchers }: BiuroTabProps) {
  const { t } = useTranslation()
  return (
    <div className="rounded-lg border border-[#e5e5e5] bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#e5e5e5] text-left">
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.dispatchers")}
            </th>
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.orders")}
            </th>
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.revenue")}
            </th>
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.distance")}
            </th>
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.roadCosts")}
            </th>
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.fuel")}
            </th>
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.drivingTime")}
            </th>
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.loadedEmpty")}
            </th>
          </tr>
        </thead>
        <tbody>
          {dispatchers.map((dispatcher) => (
            <tr
              key={dispatcher.id}
              className="border-b border-[#e5e5e5] last:border-0"
            >
              {/* Dyspozytorzy */}
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-[#737373]" />
                  <div>
                    <div className="text-sm font-medium text-[#0a0a0a]">
                      {dispatcher.name}
                    </div>
                    <div className="text-xs font-medium text-[#2563eb]">
                      {t("planner.stats.driverCount", {
                        count: dispatcher.driversCount,
                      })}
                    </div>
                  </div>
                </div>
              </td>
              {/* Zlecenia */}
              <td className="px-4 py-3">
                <span className="text-sm font-medium text-[#2563eb]">
                  {t("planner.stats.orderCount", {
                    count: dispatcher.ordersCount,
                  })}
                </span>
              </td>
              {/* Przychod */}
              <td className="px-4 py-3 text-sm text-[#0a0a0a]">
                {formatNumber(dispatcher.revenue)} €
              </td>
              {/* Dystans */}
              <td className="px-4 py-3">
                <div className="text-sm text-[#0a0a0a]">
                  {formatNumber(dispatcher.distance)} km
                </div>
                <div
                  className={cn(
                    "text-xs font-medium",
                    dispatcher.distanceDiff > 0
                      ? "text-[#dc2626]"
                      : "text-[#15803d]",
                  )}
                >
                  {dispatcher.distanceDiff > 0 ? "+" : ""}
                  {formatNumber(dispatcher.distanceDiff)} km
                </div>
              </td>
              {/* Oplaty drogowe */}
              <td className="px-4 py-3">
                <div className="text-sm text-[#0a0a0a]">
                  {formatNumber(dispatcher.roadCosts)} €
                </div>
                <div
                  className={cn(
                    "text-xs font-medium",
                    dispatcher.roadCostsDiff > 0
                      ? "text-[#dc2626]"
                      : "text-[#15803d]",
                  )}
                >
                  {dispatcher.roadCostsDiff > 0 ? "+" : ""}
                  {formatNumber(dispatcher.roadCostsDiff)} €
                </div>
              </td>
              {/* Paliwo */}
              <td className="px-4 py-3">
                <div className="text-sm text-[#0a0a0a]">
                  {formatNumber(dispatcher.fuel)} l
                </div>
                <div
                  className={cn(
                    "text-xs font-medium",
                    dispatcher.fuelDiff > 0
                      ? "text-[#dc2626]"
                      : "text-[#15803d]",
                  )}
                >
                  {dispatcher.fuelDiff > 0 ? "+" : ""}
                  {formatNumber(dispatcher.fuelDiff)} l
                </div>
              </td>
              {/* Czas jazdy */}
              <td className="px-4 py-3 text-sm text-[#0a0a0a]">
                {formatTime(dispatcher.drivingTime)}
              </td>
              {/* Ladowne / Nieladowne */}
              <td className="px-4 py-3">
                <div className="text-sm text-[#0a0a0a]">
                  {dispatcher.loadedPercent}% / {dispatcher.emptyPercent}%
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
